import React, { useState } from 'react';
import { Row, Form, Divider, Button, Drawer, Input, Checkbox } from 'antd';
import {
  FormItemWrap,
  configManager,
  useCrudEditContext,
  generateUUID
} from 'react-structure-admin';
import {
  InputPercent,
  SelectStates,
  TaxCodeSelect,
  ResCol,
  TooltipInfoIcon,
  InputNumeric,
  SearchInput
} from 'components';
import { useCompany } from 'core';
import join from 'util/functions/join';
import getStates from 'util/getStates';
import { customerTypeDisplay } from 'constants/types';
import SectionHeader from 'components/SectionHeader';
import SelectCstPisCofins from 'components/SelectCstPisCofins';

const TaxationGroupProductInvoiceRuleEdit = ({ editingRule, onClose }) => {
  const { isSimplesNacional, company } = useCompany();
  const [form] = Form.useForm();
  const { form: mainForm } = useCrudEditContext();
  const [selectedCsosn, setSelectedCsosn] = useState(editingRule.icms?.csosn);
  const allowsSnCreditRate = selectedCsosn === 101;

  const isEditing =
    editingRule?.id !== undefined || editingRule?.uuid !== undefined;

  const handleInsertOrUpdate = (values) => {
    if (!isEditing) {
      values.uuid = generateUUID();
    }

    const { rules = [] } = mainForm.getFieldsValue();
    const key = editingRule?.id ?? editingRule?.uuid;
    const index = rules.findIndex(
      (c) =>
        (c?.id !== undefined && c.id === key) ||
        (c?.uuid !== undefined && c.uuid) === key
    );

    if (index > -1) {
      rules[index] = values;
    } else {
      rules.push(values);
    }

    mainForm.setFieldsValue({ rules: [...rules] });
    form.resetFields();
    onClose();
  };

  const handleBeforeSave = ({ states: listOfStates = [], ...rest }) => {
    const { icms = {} } = rest;
    return {
      ...rest,
      states: listOfStates.map((c) => c.value),
      icms: {
        ...icms,
        snCreditRate:
          icms?.snCreditRate <= 0 || !allowsSnCreditRate
            ? null
            : icms?.snCreditRate
      }
    };
  };

  const isValid = (values) => {
    const { rules = [] } = mainForm.getFieldsValue();

    const key = editingRule?.id ?? editingRule?.uuid;
    const index = rules.findIndex(
      (c) =>
        (c?.id !== undefined && c.id === key) ||
        (c?.uuid !== undefined && c.uuid) === key
    );

    const extractAndFindDuplicates = (arr, key) => {
      const allDuplicates = arr
        .filter((_, elementIndex) => elementIndex !== index)
        .reduce((a, b) => a.concat(b[key]), values[key] ?? []);

      const uniqueValuesDuplicated = new Set(allDuplicates);
      const uniqueValuesArray = [...uniqueValuesDuplicated];

      return uniqueValuesArray;
    };

    const duplicateStates = extractAndFindDuplicates(rules, 'states');
    const duplicateCustomerTypes = extractAndFindDuplicates(
      rules,
      'customerTypes'
    );

    const duplicatesRules = rules
      .filter((_, elementIndex) => elementIndex !== index)
      .some(
        (c) =>
          c.states.some((d) => values.states.includes(d)) &&
          c.customerTypes.some((d) => values.customerTypes.includes(d))
      );

    if (duplicatesRules) {
      const state = duplicateStates
        .filter((state) => values.states.includes(state))
        .map((c) => c.toUpperCase());

      const customerType = duplicateCustomerTypes
        .filter((customerType) => values.customerTypes.includes(customerType))
        .map((c) => customerTypeDisplay[c]);

      const phraseStates =
        state.length > 1
          ? 'Já existe uma regra para os estados'
          : 'Já existe uma regra para o estado';

      const phraseCustomerTypes =
        customerType.length > 1 ? 'tipos de cliente' : 'tipo de cliente';

      form.setFields([
        {
          name: ['states'],
          errors: [
            `${phraseStates} ${join(
              state,
              ', ',
              ' e '
            )} e ${phraseCustomerTypes} ${join(customerType, ', ', ' e ')}`
          ]
        }
      ]);
    }

    return !duplicatesRules;
  };

  const handleSubmit = () => {
    form.validateFields().then((values) => {
      values = handleBeforeSave({ ...editingRule, ...values });
      if (isValid(values)) {
        handleInsertOrUpdate(values);
      }
    });
  };

  const handleBeforeBinding = ({ states: listOfStates = [], ...rest }) => {
    return {
      ...rest,
      states: listOfStates.map((c) => {
        const name = getStates(true).find((d) => d.initials === c)?.name;
        return {
          value: c,
          label: name
        };
      })
    };
  };

  const initialValues = handleBeforeBinding(editingRule);

  const supportedCustomerTypes = ['taxpayer', 'nonTaxpayer', 'foreigner'];
  const customerTypes = supportedCustomerTypes.map((type) => ({
    label: customerTypeDisplay[type],
    value: type
  }));

  return (
    <Drawer
      visible
      width={650}
      title="Regra NF-e"
      bodyStyle={{ paddingBottom: 80 }}
      onClose={onClose}
      footer={
        <div
          style={{
            position: 'absolute',
            right: 0,
            bottom: 0,
            width: '100%',
            borderTop: '1px solid #e9e9e9',
            padding: '10px 16px',
            background: '#fff',
            textAlign: 'right'
          }}
        >
          <Button onClick={onClose}>Voltar</Button>
          <Button type="primary" onClick={handleSubmit}>
            Salvar
          </Button>
        </div>
      }
    >
      <Form
        form={form}
        {...configManager.getConfig().layout.form}
        initialValues={initialValues}
      >
        <Divider orientation="left">Condições</Divider>

        <SectionHeader label="Selecione os estados" />
        <Row>
          <ResCol span={24}>
            <FormItemWrap name="states" required>
              <SelectStates mode="multiple" showForeign={true} />
            </FormItemWrap>
          </ResCol>
        </Row>

        <SectionHeader label="Tipo de cliente" />
        <Row>
          <ResCol span={24}>
            <FormItemWrap
              name="customerTypes"
              initialValue={supportedCustomerTypes}
              rules={[
                {
                  required: true,
                  message: 'Tipo de cliente é obrigatório.'
                }
              ]}
            >
              <Checkbox.Group options={customerTypes} />
            </FormItemWrap>
          </ResCol>
        </Row>

        <Divider orientation="left">Tributação</Divider>

        <SectionHeader label="ICMS" />
        {isSimplesNacional ? (
          <Row>
            <ResCol span={allowsSnCreditRate ? 11 : 17}>
              <FormItemWrap
                name={['icms', 'csosn']}
                label="CSOSN"
                required
                initialValue={102}
              >
                <TaxCodeSelect
                  type="csosn"
                  onChange={(value) => setSelectedCsosn(value)}
                />
              </FormItemWrap>
            </ResCol>
            {allowsSnCreditRate && (
              <ResCol span={6}>
                <FormItemWrap
                  label="Alíquota de Crédito"
                  name={['icms', 'snCreditRate']}
                >
                  <InputPercent decimalPlaces={company?.decimalPrecision ?? 2}/>
                </FormItemWrap>
              </ResCol>
            )}
            <ResCol span={7}>
              <FormItemWrap
                label="Cód. Benefício Fiscal"
                name={['icms', 'taxBenefitCode']}
              >
                <Input maxLength={20} />
              </FormItemWrap>
            </ResCol>
          </Row>
        ) : (
          <Row>
            <ResCol span={24}>
              <FormItemWrap
                name={['icms', 'cst']}
                label="CST"
                required
                initialValue={0}
              >
                <TaxCodeSelect type="cstIcms" />
              </FormItemWrap>
            </ResCol>
            <ResCol span={11}>
              <FormItemWrap
                label={
                  <>
                    <span>Mod det. da BC</span>
                    <TooltipInfoIcon title="Modalidade de determinação da Base de Cálculo do ICMS." />
                  </>
                }
                name={['icms', 'baseTaxModality']}
                initialValue={0}
              >
                <TaxCodeSelect type="baseTaxModalities" />
              </FormItemWrap>
            </ResCol>
            <ResCol span={7}>
              <FormItemWrap
                label="Cód. Benefício Fiscal"
                name={['icms', 'taxBenefitCode']}
              >
                <Input maxLength={20} />
              </FormItemWrap>
            </ResCol>
            <ResCol span={6}>
              <FormItemWrap label="Alíquota" name={['icms', 'rate']}>
                <InputPercent decimalPlaces={company?.decimalPrecision ?? 2}/>
              </FormItemWrap>
            </ResCol>
          </Row>
        )}

        <SectionHeader label="PIS" />
        <Row>
          <ResCol span={18}>
            <FormItemWrap
              name={['pis', 'cst']}
              label="CST"
              required
              initialValue={7}
            >
              <SelectCstPisCofins />
            </FormItemWrap>
          </ResCol>
          <ResCol span={6}>
            <FormItemWrap label="Alíquota" name={['pis', 'rate']}>
              <InputPercent  decimalPlaces={company?.decimalPrecision ?? 2}/>
            </FormItemWrap>
          </ResCol>
        </Row>

        <SectionHeader label="COFINS" />
        <Row>
          <ResCol span={18}>
            <FormItemWrap
              name={['cofins', 'cst']}
              label="CST"
              required
              initialValue={7}
            >
              <SelectCstPisCofins />
            </FormItemWrap>
          </ResCol>
          <ResCol span={6}>
            <FormItemWrap label="Alíquota" name={['cofins', 'rate']}>
              <InputPercent decimalPlaces={company?.decimalPrecision ?? 2}/>
            </FormItemWrap>
          </ResCol>
        </Row>

        <SectionHeader label="IPI" />
        <Row>
          <ResCol span={10}>
            <FormItemWrap name={['ipi', 'cst']} label="CST">
              <TaxCodeSelect type="cstIpi" allowClear />
            </FormItemWrap>
          </ResCol>
          <ResCol span={6}>
            <FormItemWrap label="Alíquota" name={['ipi', 'rate']}>
              <InputPercent decimalPlaces={company?.decimalPrecision ?? 2}/>
            </FormItemWrap>
          </ResCol>
          <ResCol span={8}>
            <FormItemWrap
              label="Cód. Enquadramento Legal"
              name={['ipi', 'classificationCode']}
            >
              <InputNumeric maxLength={3} />
            </FormItemWrap>
          </ResCol>
        </Row>

        {company.taxReformFieldsEnabled && (
          <>
            <SectionHeader label="IBS/CBS" />
            <Row>
              <ResCol span={12}>
                <FormItemWrap name={['ibsCbs', 'cst']} label="CST" required>
                  <TaxCodeSelect type="cstIbsCbs" />
                </FormItemWrap>
              </ResCol>
              <ResCol span={12}>
                <SearchInput
                  name={['ibsCbs', 'classification']}
                  label="Cód. Classificação Tributária"
                  title="Cód. Classificação Tributária"
                  resource="tax-class-codes"
                  span={24}
                  drawerSize="30%"
                  rules={[{ required: true, message: 'Selecione um código' }]}
                />
              </ResCol>
            </Row>
          </>
        )}
        <Row>
          <ResCol span={24}>
            <FormItemWrap
              label="Informações adicionais"
              name={['additionalInformation']}
              whitespace
            >
              <Input.TextArea maxLength={2000} rows={3} />
            </FormItemWrap>
          </ResCol>
        </Row>
      </Form>
    </Drawer>
  );
};

export default TaxationGroupProductInvoiceRuleEdit;
